import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Register, ResetPassword, ForgotPassword, ChangePassword, Login, ExternalAuthRequest } from '../models/account';
import { CoreService } from './core.service';
import { UserToken } from '../models/user';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  constructor(private http: HttpClient, private core: CoreService<any>) { }


  public async login(data: Login, handleError: boolean = false) {
    const response = {data: UserToken, error: null};

    try {
      const credentials: Login = {email: data.email, password: data.password, inviteToken: data.inviteToken};
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/token`, credentials));
      const token = JSON.stringify(response.data);
      localStorage.setItem('currentUser', token);
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async register(data: Register, handleError: boolean = false) {
    const response = {data: UserToken, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/register/billing/login`, data));
      const token = JSON.stringify(response.data);
      localStorage.setItem('currentUser', token);
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async resetPassword(data: ResetPassword, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/resetpassword`, data));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async forgotPassword(data: ForgotPassword, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/forgotpassword`, data));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async changePassword(data: ChangePassword, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/changepassword`, data));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async logout(handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/logout`, null));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async addClaimsToUser(id: string, data: Array<any>, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.put(`${environment.identityUrl}/account/user/${id}/assignclaims`, data));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async removeClaimsFromUser(id: string, data: Array<any>, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.put(`${environment.identityUrl}/account/user/${id}/removeclaims`, data));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async getAccountInfo() {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.identityUrl}/account/user/accountInfo`));
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async confirmEmail(code: string, userId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.identityUrl}/account/confirmEmail?code=${code}&userId=${userId}`));
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public getExternalAuthAuthorizationUrl(provider: 'google' | 'microsoft', encodedState: string): string {
    const redirectUri = encodeURIComponent(environment.externalAuthCallbackUrl);
    const state = encodeURIComponent(encodedState);
    const nonce = encodeURIComponent(Date.now().toString(36));

    if (provider === 'google') {
      const googleClientId = environment.googleOAuthClientId;
      if (!this.isClientIdConfigured(googleClientId)) {
        throw new Error('Google OAuth client ID is not configured.');
      }
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(googleClientId)}&redirect_uri=${redirectUri}&response_type=id_token&scope=openid%20email%20profile&state=${state}&nonce=${nonce}&prompt=select_account`;
    }

    const microsoftClientId = environment.microsoftOAuthClientId;
    if (!this.isClientIdConfigured(microsoftClientId)) {
      throw new Error('Microsoft OAuth client ID is not configured.');
    }
    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${encodeURIComponent(microsoftClientId)}&redirect_uri=${redirectUri}&response_type=id_token&response_mode=fragment&scope=openid%20email%20profile&state=${state}&nonce=${nonce}&prompt=select_account`;
  }

  public async completeExternalAuth(data: ExternalAuthRequest, handleError: boolean = false) {
    const response = {data: null as UserToken, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.identityUrl}/account/external/token`, data));
      const token = JSON.stringify(response.data);
      localStorage.setItem('currentUser', token);
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  private handleError(error: any) {

  }

  private isClientIdConfigured(clientId: string | undefined): boolean {
    if (!clientId) {
      return false;
    }
    const normalized = clientId.trim();
    return normalized.length > 0 && !normalized.startsWith('<') && !normalized.endsWith('>');
  }
}
